'use client';

/**
 * Tech Radar Page
 * Visualize technology adoption and make strategic decisions
 */

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { WorkspaceProvider, useWorkspace } from '@/lib/archimate/WorkspaceContext';
import { CommandCenterProvider, useCommandCenter } from '@/lib/command-center/CommandCenterContext';
import CommandCenterHeader from '@/components/command-center/CommandCenterHeader';
import TechRadar from '@/components/command-center/TechRadar';
import type {
  TechRadarEntry,
  TechRadarQuadrant,
  TechRadarRing,
  TechRadarSnapshot,
} from '@/lib/command-center/types';
import {
  TECH_RADAR_QUADRANT_LABELS,
  TECH_RADAR_RING_CONFIG,
} from '@/lib/command-center/types';

interface RadarData {
  entries: TechRadarEntry[];
  totals: {
    adopt: number;
    trial: number;
    assess: number;
    hold: number;
    total: number;
  };
}

function TechRadarContent() {
  const { currentWorkspace, loading: workspaceLoading } = useWorkspace();
  const { setWorkspaceId } = useCommandCenter();

  const [data, setData] = useState<RadarData | null>(null);
  const [snapshots, setSnapshots] = useState<TechRadarSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<'radar' | 'list' | 'history'>('radar');
  const [selectedQuadrant, setSelectedQuadrant] = useState<TechRadarQuadrant | 'all'>('all');
  const [selectedRing, setSelectedRing] = useState<TechRadarRing | 'all'>('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [adding, setAdding] = useState(false);

  // Form state
  const [newEntry, setNewEntry] = useState({
    name: '',
    description: '',
    quadrant: 'tools' as TechRadarQuadrant,
    ring: 'assess' as TechRadarRing,
    rationale: '',
    tags: '',
    url: '',
  });

  // Set workspace ID when available
  useEffect(() => {
    if (currentWorkspace?.id) {
      setWorkspaceId(currentWorkspace.id);
    }
  }, [currentWorkspace?.id, setWorkspaceId]);

  const loadData = useCallback(async () => {
    if (!currentWorkspace?.id) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch radar entries and snapshots in parallel
      const [entriesRes, snapshotsRes] = await Promise.all([
        fetch(`/api/secure/command-center/tech-radar?workspace_id=${currentWorkspace.id}`),
        fetch(`/api/secure/command-center/tech-radar/snapshots?workspace_id=${currentWorkspace.id}`),
      ]);

      if (!entriesRes.ok) throw new Error('Failed to load tech radar');
      const entriesData = await entriesRes.json();
      setData({
        entries: entriesData.entries || [],
        totals: entriesData.totals || { adopt: 0, trial: 0, assess: 0, hold: 0, total: 0 },
      });

      if (snapshotsRes.ok) {
        const snapshotsData = await snapshotsRes.json();
        setSnapshots(snapshotsData.snapshots || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Add new entry
  const handleAddEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentWorkspace?.id || !newEntry.name.trim()) return;

    try {
      setAdding(true);
      const response = await fetch('/api/secure/command-center/tech-radar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: currentWorkspace.id,
          name: newEntry.name.trim(),
          description: newEntry.description.trim() || undefined,
          quadrant: newEntry.quadrant,
          ring: newEntry.ring,
          rationale: newEntry.rationale.trim() || undefined,
          tags: newEntry.tags
            ? newEntry.tags.split(',').map((t) => t.trim()).filter(Boolean)
            : undefined,
          url: newEntry.url.trim() || undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add entry');
      }

      // Reset form and refresh
      setNewEntry({
        name: '',
        description: '',
        quadrant: 'tools',
        ring: 'assess',
        rationale: '',
        tags: '',
        url: '',
      });
      setShowAddForm(false);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add entry');
    } finally {
      setAdding(false);
    }
  };

  // Create snapshot
  const handleCreateSnapshot = async () => {
    if (!currentWorkspace?.id) return;

    const name = prompt('Enter snapshot name:');
    if (!name) return;

    try {
      const response = await fetch('/api/secure/command-center/tech-radar/snapshots', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: currentWorkspace.id,
          name,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create snapshot');
      }

      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create snapshot');
    }
  };

  // Filter entries
  const filteredEntries =
    data?.entries.filter((e) => {
      if (selectedQuadrant !== 'all' && e.quadrant !== selectedQuadrant) return false;
      if (selectedRing !== 'all' && e.ring !== selectedRing) return false;
      return true;
    }) || [];

  if (workspaceLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-foreground-tertiary text-sm mb-2">
          <Link href="/command-center" className="hover:text-accent">
            Command Center
          </Link>
          <span>/</span>
          <span>Tech Radar</span>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Tech Radar</h1>
            <p className="text-foreground-tertiary mt-1">
              Track technology adoption and make strategic decisions
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCreateSnapshot}
              className="px-4 py-2 bg-surface border border-border text-foreground rounded-lg hover:border-border/40 transition-colors"
            >
              Create Snapshot
            </button>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="px-4 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
            >
              {showAddForm ? 'Cancel' : '+ Add Entry'}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4 text-red-200 mb-6">
            {error}
          </div>
        )}

        {/* Add form */}
        {showAddForm && (
          <div className="bg-surface/50 rounded-xl border border-border-muted p-6 mb-6">
            <h3 className="text-lg font-medium text-foreground mb-4">Add Tech Radar Entry</h3>
            <form onSubmit={handleAddEntry} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-foreground-tertiary mb-1">
                    Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={newEntry.name}
                    onChange={(e) => setNewEntry({ ...newEntry, name: e.target.value })}
                    placeholder="e.g., React Query"
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-foreground-muted focus:border-accent focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-foreground-tertiary mb-1">URL</label>
                  <input
                    type="url"
                    value={newEntry.url}
                    onChange={(e) => setNewEntry({ ...newEntry, url: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-foreground-muted focus:border-accent focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-foreground-tertiary mb-1">Description</label>
                <input
                  type="text"
                  value={newEntry.description}
                  onChange={(e) => setNewEntry({ ...newEntry, description: e.target.value })}
                  placeholder="Brief description..."
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-foreground-muted focus:border-accent focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-foreground-tertiary mb-2">Quadrant</label>
                  <div className="grid grid-cols-2 gap-2">
                    {(Object.keys(TECH_RADAR_QUADRANT_LABELS) as TechRadarQuadrant[]).map((q) => (
                      <button
                        key={q}
                        type="button"
                        onClick={() => setNewEntry({ ...newEntry, quadrant: q })}
                        className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                          newEntry.quadrant === q
                            ? 'bg-accent-muted text-accent border-border'
                            : 'text-foreground-tertiary border-border-muted hover:border-border/30'
                        }`}
                      >
                        {TECH_RADAR_QUADRANT_LABELS[q]}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-foreground-tertiary mb-2">Ring</label>
                  <div className="grid grid-cols-2 gap-2">
                    {(Object.keys(TECH_RADAR_RING_CONFIG) as TechRadarRing[]).map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setNewEntry({ ...newEntry, ring: r })}
                        className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                          newEntry.ring === r
                            ? `${TECH_RADAR_RING_CONFIG[r].bgColor} ${TECH_RADAR_RING_CONFIG[r].color} border-current`
                            : 'text-foreground-tertiary border-border-muted hover:border-border/30'
                        }`}
                      >
                        {TECH_RADAR_RING_CONFIG[r].label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm text-foreground-tertiary mb-1">Rationale</label>
                <textarea
                  value={newEntry.rationale}
                  onChange={(e) => setNewEntry({ ...newEntry, rationale: e.target.value })}
                  placeholder="Why this technology at this ring..."
                  rows={2}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-foreground-muted focus:border-accent focus:outline-none resize-none"
                />
              </div>

              <div>
                <label className="block text-sm text-foreground-tertiary mb-1">Tags (comma-separated)</label>
                <input
                  type="text"
                  value={newEntry.tags}
                  onChange={(e) => setNewEntry({ ...newEntry, tags: e.target.value })}
                  placeholder="frontend, state-management, api"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-foreground-muted focus:border-accent focus:outline-none"
                />
              </div>

              <button
                type="submit"
                disabled={adding || !newEntry.name.trim()}
                className="w-full px-4 py-2 bg-accent text-accent-foreground font-medium rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {adding ? 'Adding...' : 'Add Entry'}
              </button>
            </form>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
          </div>
        )}

        {/* Content */}
        {!loading && data && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-surface/30 border border-border-muted rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-foreground">{data.totals.total}</div>
                <div className="text-sm text-foreground-tertiary">Total</div>
              </div>
              {(['adopt', 'trial', 'assess', 'hold'] as TechRadarRing[]).map((ring) => (
                <div
                  key={ring}
                  className={`${TECH_RADAR_RING_CONFIG[ring].bgColor} border border-border-muted rounded-lg p-4 text-center`}
                >
                  <div className={`text-3xl font-bold ${TECH_RADAR_RING_CONFIG[ring].color}`}>
                    {data.totals[ring]}
                  </div>
                  <div className="text-sm text-foreground-tertiary">{TECH_RADAR_RING_CONFIG[ring].label}</div>
                </div>
              ))}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-border-muted mb-6">
              <button
                onClick={() => setSelectedTab('radar')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  selectedTab === 'radar'
                    ? 'text-accent border-b-2 border-accent -mb-px'
                    : 'text-foreground-tertiary hover:text-foreground'
                }`}
              >
                Radar View
              </button>
              <button
                onClick={() => setSelectedTab('list')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  selectedTab === 'list'
                    ? 'text-accent border-b-2 border-accent -mb-px'
                    : 'text-foreground-tertiary hover:text-foreground'
                }`}
              >
                List View ({data.entries.length})
              </button>
              <button
                onClick={() => setSelectedTab('history')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  selectedTab === 'history'
                    ? 'text-accent border-b-2 border-accent -mb-px'
                    : 'text-foreground-tertiary hover:text-foreground'
                }`}
              >
                History ({snapshots.length})
              </button>
            </div>

            {/* Tab Content */}
            {selectedTab === 'radar' && (
              <div className="bg-surface/50 rounded-xl border border-border-muted p-4">
                <TechRadar
                  entries={data.entries}
                  className="h-[600px]"
                />
              </div>
            )}

            {selectedTab === 'list' && (
              <div>
                {/* Filters */}
                <div className="flex flex-wrap gap-4 mb-4">
                  <div>
                    <label className="block text-xs text-foreground-tertiary mb-1">Quadrant</label>
                    <select
                      value={selectedQuadrant}
                      onChange={(e) => setSelectedQuadrant(e.target.value as TechRadarQuadrant | 'all')}
                      className="px-3 py-1.5 bg-background border border-border rounded-lg text-foreground text-sm focus:border-accent focus:outline-none"
                    >
                      <option value="all">All Quadrants</option>
                      {(Object.keys(TECH_RADAR_QUADRANT_LABELS) as TechRadarQuadrant[]).map((q) => (
                        <option key={q} value={q}>
                          {TECH_RADAR_QUADRANT_LABELS[q]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-foreground-tertiary mb-1">Ring</label>
                    <select
                      value={selectedRing}
                      onChange={(e) => setSelectedRing(e.target.value as TechRadarRing | 'all')}
                      className="px-3 py-1.5 bg-background border border-border rounded-lg text-foreground text-sm focus:border-accent focus:outline-none"
                    >
                      <option value="all">All Rings</option>
                      {(Object.keys(TECH_RADAR_RING_CONFIG) as TechRadarRing[]).map((r) => (
                        <option key={r} value={r}>
                          {TECH_RADAR_RING_CONFIG[r].label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Entries list */}
                {filteredEntries.length === 0 ? (
                  <div className="text-center py-12 text-foreground-tertiary">
                    No entries found
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredEntries.map((entry) => (
                      <div
                        key={entry.id}
                        className="bg-surface/30 border border-border-muted rounded-lg p-4 hover:border-border transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-foreground">{entry.name}</span>
                              <span
                                className={`px-2 py-0.5 text-xs rounded ${TECH_RADAR_RING_CONFIG[entry.ring].bgColor} ${TECH_RADAR_RING_CONFIG[entry.ring].color}`}
                              >
                                {TECH_RADAR_RING_CONFIG[entry.ring].label}
                              </span>
                              {entry.movement !== 'none' && entry.movement !== 'new' && (
                                <span className="text-xs text-foreground-tertiary">
                                  {entry.movement === 'up' ? '↑' : '↓'}
                                </span>
                              )}
                              {entry.movement === 'new' && (
                                <span className="text-xs text-accent">★ New</span>
                              )}
                            </div>
                            <div className="text-xs text-foreground-tertiary mb-1">
                              {TECH_RADAR_QUADRANT_LABELS[entry.quadrant]}
                            </div>
                            {entry.description && (
                              <p className="text-sm text-foreground-secondary mb-2">{entry.description}</p>
                            )}
                            {entry.rationale && (
                              <p className="text-xs text-foreground-tertiary italic mb-2">{entry.rationale}</p>
                            )}
                            {entry.tags && entry.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {entry.tags.map((tag) => (
                                  <span
                                    key={tag}
                                    className="px-1.5 py-0.5 text-xs bg-cream/10 text-foreground-tertiary rounded"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          {entry.url && (
                            <a
                              href={entry.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-accent hover:text-accent text-sm"
                            >
                              →
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {selectedTab === 'history' && (
              <div>
                {snapshots.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-4xl mb-4">📸</div>
                    <p className="text-foreground-tertiary">No snapshots yet</p>
                    <p className="text-foreground-muted text-sm mt-1">
                      Create snapshots to track radar changes over time
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {snapshots.map((snapshot) => (
                      <div
                        key={snapshot.id}
                        className="bg-surface/30 border border-border-muted rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-medium text-foreground">{snapshot.name}</h3>
                            <p className="text-xs text-foreground-tertiary">
                              {new Date(snapshot.snapshot_date).toLocaleDateString()}
                            </p>
                          </div>
                          <span className="text-sm text-foreground-tertiary">
                            {snapshot.entry_count} entries
                          </span>
                        </div>
                        {snapshot.description && (
                          <p className="text-sm text-foreground-tertiary mb-3">{snapshot.description}</p>
                        )}
                        <div className="grid grid-cols-4 gap-2">
                          <div className={`p-2 rounded text-center ${TECH_RADAR_RING_CONFIG.adopt.bgColor}`}>
                            <div className={`text-lg font-bold ${TECH_RADAR_RING_CONFIG.adopt.color}`}>
                              {snapshot.adopt_count}
                            </div>
                            <div className="text-xs text-foreground-tertiary">Adopt</div>
                          </div>
                          <div className={`p-2 rounded text-center ${TECH_RADAR_RING_CONFIG.trial.bgColor}`}>
                            <div className={`text-lg font-bold ${TECH_RADAR_RING_CONFIG.trial.color}`}>
                              {snapshot.trial_count}
                            </div>
                            <div className="text-xs text-foreground-tertiary">Trial</div>
                          </div>
                          <div className={`p-2 rounded text-center ${TECH_RADAR_RING_CONFIG.assess.bgColor}`}>
                            <div className={`text-lg font-bold ${TECH_RADAR_RING_CONFIG.assess.color}`}>
                              {snapshot.assess_count}
                            </div>
                            <div className="text-xs text-foreground-tertiary">Assess</div>
                          </div>
                          <div className={`p-2 rounded text-center ${TECH_RADAR_RING_CONFIG.hold.bgColor}`}>
                            <div className={`text-lg font-bold ${TECH_RADAR_RING_CONFIG.hold.color}`}>
                              {snapshot.hold_count}
                            </div>
                            <div className="text-xs text-foreground-tertiary">Hold</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function TechRadarPage() {
  return (
    <WorkspaceProvider>
      <CommandCenterProvider>
        <CommandCenterHeader />
        <TechRadarContent />
      </CommandCenterProvider>
    </WorkspaceProvider>
  );
}
